/**
 * MULLED WINDOWS (Matt, 2026-10-08): windows touching side by side on one wall can be
 * joined into a MULLED UNIT — selected and moved as one, tagged once, scheduled as one
 * combined elevation — and broken apart again. Doors never mull (a door's side lites and
 * transoms are its own params).
 *
 * How manufacturers do it (researched, 2026-10-08 — Marvin/Pella/Ply Gem mulling docs):
 * each unit KEEPS ITS OWN FRAME; frames are fastened jamb-to-jamb (a "standard mull" has
 * no space between them; some lines add a 1/2" mull joint). There is no shared jamb, so
 * the geometry keeps both jambs and the unit's outside width is the sum of the frames.
 *
 * Stored per member (instance params, the casework-run precedent):
 *   mullGroupId — shared id; mullIndex — left→right order along the host.
 * The unit's layout is a STANDING RULE (post-transact hook, like EQ dimensions): when any
 * member's position or size changes in a transaction, the whole unit re-packs frame to
 * frame around the member that changed — so every move gesture (grip, nudge, typed
 * dimension, EQ, Align, Move) moves the unit, and resizing one unit pushes its neighbours.
 * Membership heals itself: a unit left with fewer than two members dissolves.
 */
import * as Y from 'yjs'
import { getElement, listElements, maps, registerPostTransactHook, resolveElement, transact, type ModelDoc } from './doc'
import { resolveOpeningFrame } from './openingFrame'
import { newElementId } from './ids'
import type { ElementId, ParamValue } from './types'

/** frames closer than this read as touching (mullable) */
export const MULL_TOUCH_FT = 0.5 / 12
/** the gap between mulled frames — a standard mull: none */
export const MULL_JOINT_FT = 0

/** a window's outside frame width (unit + both jambs, rounded to 1/8") — the kernel twin
 *  of bim-geometry's totalOpeningWidth for windows */
export function windowOuterWidthFt(params: Record<string, ParamValue>, typeParams: Record<string, ParamValue> = {}): number {
  const w = (params.widthFt as number) || 3
  const fw = resolveOpeningFrame('window', { ...typeParams, ...params }).widthFt
  const total = w + 2 * fw
  const r = Math.round(total * 96) / 96
  return Math.abs(r - total) < 1e-9 ? total : r
}

interface Span { id: ElementId; s: number; w: number; z0: number; z1: number; hostId: string }
function spanOf(model: ModelDoc, id: ElementId): Span | null {
  const el = resolveElement(model, id)
  if (!el || el.category !== 'window' || !el.hostId) return null
  const w = windowOuterWidthFt(el.params, el.typeParams)
  const z0 = (el.params.sillFt as number) || 0
  return { id, s: (el.params.hostOffsetFt as number) || 0, w, z0, z1: z0 + ((el.params.heightFt as number) || 4), hostId: el.hostId }
}

/** the window plus every window touching it in a chain on the same wall (frames within
 *  MULL_TOUCH_FT, heights overlapping), ordered along the wall. Doors are never included. */
export function touchingWindows(model: ModelDoc, id: ElementId): ElementId[] {
  const start = spanOf(model, id)
  if (!start) return []
  const pool = listElements(model)
    .filter((e) => e.category === 'window' && e.hostId === start.hostId)
    .map((e) => spanOf(model, e.id)).filter((x): x is Span => !!x)
  const group = [start]
  let grew = true
  while (grew) {
    grew = false
    for (const o of pool) {
      if (group.some((g) => g.id === o.id)) continue
      const hit = group.some((g) => {
        const gap = Math.max(o.s - o.w / 2 - (g.s + g.w / 2), g.s - g.w / 2 - (o.s + o.w / 2))
        return gap <= MULL_TOUCH_FT && Math.min(o.z1, g.z1) - Math.max(o.z0, g.z0) > 0.05
      })
      if (hit) { group.push(o); grew = true }
    }
  }
  return group.sort((a, b) => a.s - b.s).map((g) => g.id)
}

/** the members of a mulled unit, left → right */
export function mullMembers(model: ModelDoc, groupId: string): ElementId[] {
  return listElements(model)
    .filter((e) => e.category === 'window' && e.params.mullGroupId === groupId)
    .sort((a, b) => ((a.params.mullIndex as number) ?? 0) - ((b.params.mullIndex as number) ?? 0))
    .map((e) => e.id)
}

/** the unit a window belongs to, or null */
export function mullGroupOf(model: ModelDoc, id: ElementId): string | null {
  const g = getElement(model, id)?.params.mullGroupId
  return typeof g === 'string' && g ? g : null
}

/** every element id in the selection, widened to whole mulled units */
export function expandMullGroups(model: ModelDoc, ids: ElementId[]): ElementId[] {
  const out: ElementId[] = []
  const seen = new Set<ElementId>()
  for (const id of ids) {
    const g = mullGroupOf(model, id)
    for (const m of g ? mullMembers(model, g) : [id]) if (!seen.has(m)) { seen.add(m); out.push(m) }
  }
  return out
}

/** the station (along the host) at the CENTER of a mulled unit's outside frames */
export function mullCenterStation(model: ModelDoc, groupId: string): number | null {
  const sp = mullMembers(model, groupId).map((id) => spanOf(model, id)).filter((x): x is Span => !!x)
  if (!sp.length) return null
  const lo = Math.min(...sp.map((s) => s.s - s.w / 2)), hi = Math.max(...sp.map((s) => s.s + s.w / 2))
  return (lo + hi) / 2
}

/** pack a unit frame to frame around `anchor` (which stays put) — writes hostOffsetFt */
function repack(model: ModelDoc, members: ElementId[], anchor: ElementId): void {
  const spans = members.map((id) => spanOf(model, id))
  if (spans.some((s) => !s)) return
  const sp = spans as Span[]
  const k = Math.max(0, sp.findIndex((s) => s.id === anchor))
  const centers = sp.map((s) => s.s)
  for (let i = k - 1; i >= 0; i--) centers[i] = centers[i + 1] - (sp[i + 1].w + sp[i].w) / 2 - MULL_JOINT_FT
  for (let i = k + 1; i < sp.length; i++) centers[i] = centers[i - 1] + (sp[i - 1].w + sp[i].w) / 2 + MULL_JOINT_FT
  const els = maps(model).elements
  sp.forEach((s, i) => {
    if (Math.abs(centers[i] - s.s) < 1e-9) return
    const pm = els.get(s.id)?.get('params') as Y.Map<unknown> | undefined
    pm?.set('hostOffsetFt', centers[i])
  })
}

/**
 * Mull the selected window with every window touching it in a chain. Packs the unit
 * frame to frame (the leftmost stays put). Returns the unit's id, or a reason it can't.
 */
export function mullWindows(model: ModelDoc, id: ElementId): { ok: true; groupId: string; count: number } | { ok: false; reason: string } {
  const el = getElement(model, id)
  if (!el) return { ok: false, reason: 'Nothing selected.' }
  if (el.category === 'door') return { ok: false, reason: 'Doors don’t mull — use the door’s side lites and transom instead.' }
  if (el.category !== 'window') return { ok: false, reason: 'Only windows mull.' }
  const chain = touchingWindows(model, id)
  if (chain.length < 2) return { ok: false, reason: 'No window is touching this one on the same wall — slide them frame to frame first.' }
  if (chain.some((m) => getElement(model, m)?.params.pinned === true)) return { ok: false, reason: 'A window in this run is pinned — unpin it first.' }
  const groupId = newElementId() as string
  transact(model, () => {
    const els = maps(model).elements
    chain.forEach((m, i) => {
      const pm = els.get(m)?.get('params') as Y.Map<unknown> | undefined
      pm?.set('mullGroupId', groupId)
      pm?.set('mullIndex', i)
    })
    repack(model, chain, chain[0])
  })
  return { ok: true, groupId, count: chain.length }
}

/** break a mulled unit back into separate windows (positions unchanged) */
export function breakMull(model: ModelDoc, groupId: string): number {
  const members = mullMembers(model, groupId)
  transact(model, () => {
    const els = maps(model).elements
    for (const m of members) {
      const pm = els.get(m)?.get('params') as Y.Map<unknown> | undefined
      pm?.delete('mullGroupId')
      pm?.delete('mullIndex')
    }
  })
  return members.length
}

const LAYOUT_KEYS = new Set(['hostOffsetFt', 'widthFt', 'frameProduct', 'mullIndex', 'mullGroupId'])

/** the standing rule: re-pack any unit whose member moved/resized; dissolve units of one */
function mullRule(model: ModelDoc, tr?: Y.Transaction): void {
  if (!tr) return
  const els = maps(model).elements
  const touched = new Map<string, ElementId>()   // groupId → first changed member
  let membership = false
  tr.changed.forEach((keys, type) => {
    if (type === (els as unknown)) { membership = true; return }
    // params map → its element record → the elements map
    const rec = type.parent as Y.AbstractType<unknown> | null
    if (!rec || rec.parent !== (els as unknown)) return
    let layout = false
    keys.forEach((k) => { if (k && LAYOUT_KEYS.has(k)) layout = true })
    if (!layout) return
    const id = rec._item?.parentSub as ElementId | undefined
    const g = id ? mullGroupOf(model, id) : null
    if (g && !touched.has(g)) touched.set(g, id!)
  })
  if (!touched.size && !membership) return
  // a unit that lost members (deleted, or a lone pasted member) dissolves below two
  const counts = new Map<string, ElementId[]>()
  for (const e of listElements(model)) {
    const g = e.category === 'window' ? e.params.mullGroupId : null
    if (typeof g === 'string' && g) counts.set(g, [...(counts.get(g) ?? []), e.id])
  }
  for (const [g, ids] of counts) {
    if (ids.length >= 2) continue
    for (const m of ids) {
      const pm = els.get(m)?.get('params') as Y.Map<unknown> | undefined
      pm?.delete('mullGroupId'); pm?.delete('mullIndex')
    }
    touched.delete(g)
  }
  for (const [g, anchor] of touched) {
    const members = mullMembers(model, g)
    if (members.length >= 2) repack(model, members, anchor)
  }
}

registerPostTransactHook(mullRule)
